/**
 * Invoice Service
 * Handles invoice generation and payment operations
 */

import { apiClient } from './api';
import {
  ensureInvoiceResponse,
  ensureInvoicesListResponse,
  ensureInvoiceSummary,
  ensureFinalPaymentResponse,
} from '@/lib/api/type-guards';

// Invoice status enum
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

// Line item for invoice
export interface InvoiceLineItem {
  material_type: string;
  quantity: number; // tons
  unit_price_cents: number; // cents per ton
  total_cents: number; // quantity * unit_price
  is_diverted: boolean;
  ticket_ids: string[]; // Which tickets contributed to this line
}

// Full invoice
export interface Invoice {
  id: string;
  job_id: string;
  invoice_number: string | null;
  ticket_ids: string[]; // Material ticket IDs
  total_amount_cents: number;
  line_items: InvoiceLineItem[];
  status: InvoiceStatus;
  payment_id: string | null;
  is_partial: boolean;
  notes: string | null;
  created_by_provider_id: string | null;
  created_at: number;
  sent_at: number | null;
  paid_at: number | null;
}

// Material ticket (simplified from job service)
export interface MaterialTicket {
  id: string;
  job_id: string;
  ticket_number?: string;
  ticket_date: number;
  facility_name: string;
  materials: Array<{
    material_type: string;
    weight_tons: number;
    is_diverted: boolean;
  }>;
}

// Create invoice params
export interface CreateInvoiceParams {
  job_id: string;
  ticket_ids: string[];
  is_partial?: boolean;
  notes?: string;
}

// Invoice summary for display
export interface InvoiceSummary {
  invoice: Invoice;
  job: {
    project_name: string;
    applicant_email: string;
  };
  service_provider: {
    name: string;
    contact_email: string;
  };
  deposit_payment?: {
    amount_cents: number;
    status: string;
  };
  balance_due_cents: number; // total - deposit
}

// Final payment response
export interface FinalPaymentResponse {
  payment_intent_id: string;
  client_secret: string | null;
  amount_cents: number;
}

// Response types
interface InvoiceResponse {
  invoice: Invoice;
}

interface InvoiceSummaryResponse extends InvoiceSummary {}

interface InvoicesListResponse {
  invoices: Invoice[];
}

export const invoiceService = {
  /**
   * Generate invoice from material tickets
   */
  async generateInvoice(params: CreateInvoiceParams): Promise<Invoice> {
    const response = await apiClient.post<InvoiceResponse>('/invoices', params);
    return ensureInvoiceResponse(response);
  },

  /**
   * Get invoice by ID with full summary
   */
  async getInvoice(invoiceId: string): Promise<InvoiceSummary> {
    const response = await apiClient.get<InvoiceSummaryResponse>(`/invoices/${invoiceId}`);
    return ensureInvoiceSummary(response);
  },

  /**
   * Get all invoices for a job
   */
  async getJobInvoices(jobId: string): Promise<Invoice[]> {
    const response = await apiClient.get<InvoicesListResponse>(`/jobs/${jobId}/invoices`);
    return ensureInvoicesListResponse(response);
  },

  /**
   * Send invoice to applicant (mark as sent)
   */
  async sendInvoice(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.post<InvoiceResponse>(`/invoices/${invoiceId}/send`, {});
    return ensureInvoiceResponse(response);
  },

  /**
   * Create payment intent for final balance
   */
  async createFinalPayment(invoiceId: string): Promise<FinalPaymentResponse> {
    const response = await apiClient.post<FinalPaymentResponse>(`/invoices/${invoiceId}/pay`, {});
    return ensureFinalPaymentResponse(response);
  },
};
